/**
=========================================================
* Trust Cargo - add dialog
=========================================================

* Every "add" happens in a dialog over the current page, so you never lose
* your place - your filters, your page number and your scroll position are
* all still there when it closes.
*
* On success it calls onCreated(), which is how the list behind it refreshes
* without a navigation.
*/

import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import entities from "components/AddDialog/entities";

/**
 * Shared field sizing. Kept in one place so the text inputs, the date
 * pickers and the search boxes all end up the same height - mixing MUI
 * densities is what makes a form look thrown together.
 */
const FIELD_SX = {
  "& .MuiInputBase-root": {
    minHeight: 52,
    fontSize: 15,
  },
  "& .MuiInputBase-input": {
    padding: "14px 14px",
  },
  "& .MuiFormHelperText-root": {
    marginTop: 2,
    fontSize: 12,
  },
};

/**
 * The "+ Add" row at the bottom of a manageable dropdown, marked so it can
 * never be confused with a real choice. It carries the typed text with it,
 * and nothing that can be typed into the box can produce this prefix.
 */
const NEW_OPTION = "\u0000new:";

const matchOptions = createFilterOptions();

/**
 * A date as an <input type="date"> understands it.
 *
 * Some endpoints send a date column back as a full ISO timestamp -
 * "2026-06-17T00:00:00.000000Z" for a day with no time in it. The input
 * shows nothing at all for that, so the box looked empty while still
 * holding the value, and saving handed MySQL a datetime for a DATE column:
 * "Incorrect date value ... for column 'loading_date'".
 *
 * Anything that does not start with a plain date is left exactly as it is.
 */
const asDate = (value) => {
  const text = String(value ?? "");
  const day = text.match(/^(\d{4}-\d{2}-\d{2})/);

  return day ? day[1] : text;
};

/** The API's own words for what went wrong, when it sent any. */
const apiMessage = (err, fallback) =>
  String(err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback);

function AddDialog({ open, entity, onClose, onCreated, recordId, record, defaults, tabs, extra }) {
  /**
   * One dialog can carry more than one form. The people list opens a person
   * and that person's customer profile behind a single "edit" button, rather
   * than two buttons per row that both said the same word - which form you
   * are on is written across the top instead.
   */
  const [tab, setTab] = useState(0);
  const sheets = tabs || [];

  // the form in front - or the plain single-form props, when there are no tabs
  const current = sheets[tab] || sheets[0] || { entity, recordId, record, defaults };

  const config = entities[current.entity];
  const isEdit = Boolean(current.recordId);

  const [values, setValues] = useState({});
  const [options, setOptions] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [serverError, setServerError] = useState("");
  // why the dropdowns are empty, when they are
  const [optionsError, setOptionsError] = useState("");

  /**
   * Editing the choices themselves, not the record.
   *
   * `optionsNonce` is bumped to re-read a list after it has been changed;
   * `manage` is the little sheet over the box - {mode, field, id, value} -
   * that asks for the new wording or for a deletion to be confirmed; and
   * `openBox` is which dropdown is showing, held here so it can be closed
   * before that sheet takes the focus.
   */
  const [optionsNonce, setOptionsNonce] = useState(0);
  const [manage, setManage] = useState(null);
  const [manageBusy, setManageBusy] = useState(false);
  const [manageError, setManageError] = useState("");
  const [openBox, setOpenBox] = useState(null);

  /**
   * Somebody who is not on a dropdown yet, being created from it.
   *
   * `creating` is the field and the name that was typed, which opens the
   * form for that record over this one. `pendingPick` survives the save: the
   * list is re-read afterwards, and whoever came back under that name is the
   * one the box is left showing - the create endpoints answer with a token
   * rather than a row, so there is no id to go on.
   */
  const [creating, setCreating] = useState(null);
  const [pendingPick, setPendingPick] = useState(null);

  // start clean every time it opens, and pull any dropdown choices
  useEffect(() => {
    if (!open) {
      setTab(0);
      // a closed dialog is starting over: the next opening reads its lists
      // afresh behind the loader, with nothing half-edited left over
      setOptionsNonce(0);
      setManage(null);
      setManageError("");
      setOpenBox(null);
      return;
    }
    if (!config) return;

    /**
     * Starting values for a new record, in order of precedence:
     *   config.defaults - constants the API needs but nobody types
     *   defaults prop   - what the calling screen already knows
     */
    setValues({ ...(config.defaults || {}), ...(current.defaults || {}) });
    setErrors({});
    setServerError("");

    // drop nulls so inputs stay controlled with ""
    const dates = new Set(config.fields.filter((f) => f.type === "date").map((f) => f.name));

    const prefill = (rec) => {
      const clean = {};
      Object.keys(rec).forEach((k) => {
        if (rec[k] === null || rec[k] === undefined) {
          clean[k] = "";
          return;
        }
        clean[k] = dates.has(k) ? asDate(rec[k]) : rec[k];
      });
      setValues(clean);
    };

    /**
     * The row is often already in hand from the list that opened this dialog.
     * Passing it in skips a round trip - and is the only way to edit a post,
     * whose show endpoint is broken on the server.
     */
    if (current.recordId && current.record) {
      prefill(current.record);
    } else if (current.recordId) {
      /**
       * Two conventions live here. The v2 endpoints are REST-shaped
       * (GET /orders/5) and return the record directly. The legacy ones are
       * GET /specefic_user/5 and wrap it as {user: {...}}. `config.edit`
       * describes the legacy case; without it the REST shape is assumed.
       */
      const url = config.edit?.show
        ? config.edit.show(current.recordId)
        : `${config.endpoint}/${current.recordId}`;

      fetch(url, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => {
          const rec = config.edit?.unwrap ? json?.[config.edit.unwrap] : json;

          if (!rec) {
            setServerError("Could not load the record");
            return;
          }
          prefill(rec);
        })
        .catch(() => setServerError("Could not load the record"));
    }
  }, [open, current.entity, current.recordId]);

  /**
   * The dropdown choices, loaded on their own.
   *
   * Kept apart from the effect above because a list can change while the
   * form is open - a telex wording added, renamed or deleted from the box -
   * and re-reading it must not reset what has already been typed into the
   * other fields. Bumping `optionsNonce` is how those actions ask for it.
   */
  useEffect(() => {
    if (!open || !config) return;

    const selects = config.fields.filter((f) => f.optionsFrom);
    if (selects.length === 0) return;

    // only the first read blanks the form for a loader; a reload after an
    // edit happens under the open dropdown, which must stay where it is
    if (optionsNonce === 0) setLoadingOptions(true);
    setOptionsError("");

    /**
     * Every failure here used to be swallowed into an empty array, so a dead
     * endpoint looked exactly like a legitimately empty dropdown. Now the
     * reason is captured and shown.
     *
     * Requests are made once per URL rather than once per field - the order
     * form has three selects that all read the same endpoint.
     */
    const urls = [...new Set(selects.map((f) => f.optionsFrom.url))];

    Promise.all(
      urls.map(async (url) => {
        try {
          const res = await fetch(url, { cache: "no-store" });
          if (!res.ok) {
            return [url, null, `HTTP ${res.status}`];
          }
          return [url, await res.json(), null];
        } catch (e) {
          return [url, null, e?.message || "network error"];
        }
      })
    ).then((results) => {
      const byUrl = {};
      const problems = [];

      results.forEach(([url, json, err]) => {
        byUrl[url] = json;
        if (err) problems.push(`${url.split("/").pop()}: ${err}`);
      });

      const next = {};
      selects.forEach((f) => {
        const json = byUrl[f.optionsFrom.url];
        if (!json) {
          next[f.name] = [];
          return;
        }

        const list = f.optionsFrom.path ? json[f.optionsFrom.path] : json;

        if (!Array.isArray(list)) {
          // the endpoint answered but not in the shape this field expects
          problems.push(`${f.label}: no "${f.optionsFrom.path}" in response`);
          next[f.name] = [];
          return;
        }

        next[f.name] = list;
      });

      setOptions(next);

      /**
       * A value the form decides rather than asks for - a new order is
       * always NEW. It waits for the lists because the value is looked up
       * in one (statuses are rows the office keeps, so the id is not known
       * ahead of time), and it only ever applies to a new record: on edit
       * the field shows what was saved.
       */
      if (!isEdit) {
        const fixed = {};
        config.fields.forEach((f) => {
          if (typeof f.createValue !== "function") return;
          const v = f.createValue(next[f.name] || []);
          if (v !== undefined && v !== null && v !== "") fixed[f.name] = v;
        });
        if (Object.keys(fixed).length > 0) setValues((prev) => ({ ...prev, ...fixed }));
      }

      /**
       * The row just created, chosen now that the list has it. Matched by
       * name and taking the highest id, because ids ascend and the newest
       * of a repeated name is the one that was made a moment ago.
       */
      if (pendingPick) {
        const f = config.fields.find((x) => x.name === pendingPick.field);
        const list = next[pendingPick.field] || [];

        if (f && f.optionsFrom) {
          const vKey = f.optionsFrom.value;
          const lKey = f.optionsFrom.label;

          const hit = list
            .filter((o) => String(o[lKey] ?? "").trim() === pendingPick.label)
            .sort((a, b) => Number(a[vKey]) - Number(b[vKey]))
            .pop();

          if (hit) setValues((prev) => ({ ...prev, [pendingPick.field]: hit[vKey] }));
        }

        setPendingPick(null);
      }

      setOptionsError(problems.join(" · "));
      setLoadingOptions(false);
    });
  }, [open, current.entity, optionsNonce]);

  if (!config) return null;

  const setField = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  /**
   * A choice can prefill another field from the option that was picked -
   * choosing a customer fills in the next free shop code for them.
   * The target stays editable; this is a suggestion, not a lock.
   */
  const applyFills = (field, chosen) => {
    if (!field.fills || !chosen) return;

    const { target, from, compute } = field.fills;
    // `compute` lets the entity work the value out from the whole option
    // rather than reading a single key off it
    const value = compute ? compute(chosen) : chosen[from];

    setValues((v) => ({ ...v, [target]: value ?? "" }));
    setErrors((e) => ({ ...e, [target]: undefined }));
  };

  /**
   * Keeping the list of choices, from the box that offers it.
   *
   * These write to the list only - never to a container from here. What the
   * server does to containers already carrying a renamed or deleted wording
   * is its business, and it says so in the message it sends back.
   */
  const reloadOptions = () => setOptionsNonce((n) => n + 1);

  const closeManage = () => {
    setManage(null);
    setManageError("");
  };

  const addOption = async (field, text) => {
    const value = String(text || "").trim();
    if (!value) return;

    setManageBusy(true);
    setManageError("");

    try {
      await axios.post(field.manage.url, { field: field.manage.field, value });
      // what was just added is what you meant to pick
      setField(field.name, value);
      reloadOptions();
      closeManage();
    } catch (err) {
      setManageError(apiMessage(err, `Could not add "${value}"`));
    }

    setManageBusy(false);
  };

  const renameOption = async (field, id, was, text) => {
    const value = String(text || "").trim();
    if (!value || value === was) {
      closeManage();
      return;
    }

    setManageBusy(true);
    setManageError("");

    try {
      await axios.put(`${field.manage.url}/${id}`, { value });
      // the box was showing the old wording - keep it on the same thing
      if (String(values[field.name] ?? "") === String(was)) setField(field.name, value);
      reloadOptions();
      closeManage();
    } catch (err) {
      setManageError(apiMessage(err, "Could not rename it"));
    }

    setManageBusy(false);
  };

  const deleteOption = async (field, id, was) => {
    setManageBusy(true);
    setManageError("");

    try {
      await axios.delete(`${field.manage.url}/${id}`);
      if (String(values[field.name] ?? "") === String(was)) setField(field.name, "");
      reloadOptions();
      closeManage();
    } catch (err) {
      setManageError(apiMessage(err, "Could not delete it"));
    }

    setManageBusy(false);
  };

  const validate = () => {
    const found = {};
    config.fields.forEach((f) => {
      // a password is required to create an account but blank on edit means
      // "leave it as it is" - demanding it would force a reset every time
      if (isEdit && f.optionalOnEdit) return;
      // hidden on create, so there is nothing the user could have typed
      if (!isEdit && f.editOnly) return;

      // required only in some states - the customer form asks for an email
      // and a password when it is making the account, and for neither when
      // an existing user was picked instead
      const needed =
        typeof f.requiredWhen === "function" ? f.requiredWhen(values) : Boolean(f.required);

      if (needed && !String(values[f.name] ?? "").trim()) {
        // a field required only in some states usually needs to say why
        found[f.name] = f.requiredMessage || "Required";
      }
    });
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;

    setSaving(true);
    setServerError("");

    try {
      /**
       * Work the record cannot be created without, done first and allowed to
       * change what gets sent: a customer with no user picked has the account
       * made here, and what comes back is the same form values carrying the
       * id of it.
       *
       * A failure stops the save. That is the point - a customer row pointing
       * at an account that was never made is worse than no customer row.
       */
      const prepared = !isEdit && config.beforeSubmit ? await config.beforeSubmit(values) : values;

      /**
       * Dates are normalised again here rather than trusted from the boxes:
       * a value can reach the form from a record handed in by the screen
       * that opened it, and one carrying a time is refused by the column it
       * is going into.
       */
      const toSend = { ...prepared };
      config.fields.forEach((f) => {
        if (f.type === "date" && toSend[f.name]) toSend[f.name] = asDate(toSend[f.name]);
      });

      const e = config.edit || {};
      // legacy endpoints want the id in the body; v2 ones carry it in the URL
      const withId = isEdit && e.sendId;

      let payload = toSend;
      let headers = { Accept: "application/json", "Content-Type": "application/json" };

      // forms carrying files must go up as multipart; the API expects the
      // repeated-index shape image[0], image[1] ...
      if (config.multipart) {
        const fd = new FormData();
        config.fields.forEach((f) => {
          const v = toSend[f.name];
          if (v === undefined || v === null || v === "") return;

          if (f.type === "file") {
            Array.from(v).forEach((file, i) => fd.append(`${f.name}[${i}]`, file));
          } else {
            fd.append(f.name, v);
          }
        });
        if (withId) fd.append("id", current.recordId);
        payload = fd;
        headers = { Accept: "application/json" };
      } else if (withId) {
        payload = { ...toSend, id: current.recordId };
      }

      if (isEdit) {
        // `submit` may be a fixed URL or built from the id (edit_post/{id})
        let url = `${config.endpoint}/${current.recordId}`;
        if (typeof e.submit === "function") url = e.submit(current.recordId);
        else if (e.submit) url = e.submit;

        if ((e.method || "put").toLowerCase() === "post") {
          await axios.post(url, payload, { headers });
        } else {
          await axios.put(url, payload, { headers });
        }
      } else {
        const created = await axios.post(config.endpoint, payload, { headers });

        // entity-specific follow-up work, e.g. an order leaving a supplier of
        // the same shop code behind. It must never turn a saved record into a
        // visible failure, so its own errors are its business.
        if (config.afterCreate) {
          await config.afterCreate(toSend, created?.data);
        }
      }
      setSaving(false);
      // what was saved goes back with the call: a form opened from inside a
      // dropdown has to find the row it just made, and the create endpoints
      // answer with a token or a status rather than the record
      onCreated(toSend);
      onClose();
    } catch (err) {
      setSaving(false);
      // surface the API's own message rather than a generic failure
      setServerError(apiMessage(err, "Could not save"));

      /**
       * Laravel says which field it turned down and why. Without this the
       * form showed "The given data was invalid." over a page of boxes and
       * left you to guess which one it meant.
       */
      const refused = err?.response?.data?.errors;

      if (refused && typeof refused === "object") {
        const byField = {};
        Object.keys(refused).forEach((name) => {
          const said = refused[name];
          byField[name] = Array.isArray(said) ? said[0] : String(said);
        });
        setErrors((prev) => ({ ...prev, ...byField }));
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      // the order form has 15 fields; a wider sheet keeps two roomy columns
      // instead of two cramped ones
      maxWidth={config.wide ? "lg" : "sm"}
      PaperProps={{
        sx: ({ borders, boxShadows }) => ({
          borderRadius: borders.borderRadius.lg,
          boxShadow: boxShadows.xl,
        }),
      }}
    >
      <DialogTitle
        sx={({ palette, functions: { rgba } }) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${rgba(palette.dark.main, 0.08)}`,
          pb: 1.5,
        })}
      >
        <SuiTypography variant="h5" fontWeight="bold">
          {isEdit ? `Edit - ${config.title.replace(/^(Add|Create)\s*/, "")}` : config.title}
        </SuiTypography>
        <Icon
          onClick={saving ? undefined : onClose}
          sx={({ palette, functions: { rgba } }) => ({
            cursor: saving ? "default" : "pointer",
            fontSize: 26,
            color: rgba(palette.dark.main, 0.45),
            "&:hover": { color: palette.error.main },
          })}
        >
          close
        </Icon>
      </DialogTitle>

      {/* which record you are working on, spelled out - the row itself has
          one button, so this is where "user" and "customer" is decided */}
      {sheets.length > 1 && (
        <Tabs
          value={tab}
          onChange={(e, next) => setTab(next)}
          variant="fullWidth"
          sx={({ palette, functions: { rgba } }) => ({
            px: 2,
            minHeight: 48,
            borderBottom: `1px solid ${rgba(palette.dark.main, 0.08)}`,
            "& .MuiTab-root": {
              minHeight: 48,
              fontSize: 14,
              fontWeight: 700,
              textTransform: "none",
              color: rgba(palette.dark.main, 0.55),
            },
            "& .Mui-selected": { color: `${palette.dark.main} !important` },
          })}
        >
          {sheets.map((sheet) => (
            <Tab key={sheet.entity} label={sheet.label} disabled={Boolean(sheet.disabled)} />
          ))}
        </Tabs>
      )}

      {/**
       * A choice that belongs at the head of the form rather than in the run
       * of fields: payment type decides what the order IS - a we_pay one is
       * put on a workflow the other never sees - so it is answered before
       * anything is typed, as two tabs across the top.
       *
       * Full width and hard against each other, like the sheet tabs above
       * them, so they read as one control with two states rather than as two
       * buttons somebody has to compare.
       */}
      {!loadingOptions &&
        config.fields
          .filter((f) => f.type === "choice" && f.atTop && (isEdit || !f.editOnly))
          .map((f) => {
            const chosen = String(values[f.name] ?? "");

            return (
              <SuiBox
                key={f.name}
                display="flex"
                sx={({ palette, functions: { rgba } }) => ({
                  borderBottom: `1px solid ${rgba(palette.dark.main, 0.08)}`,
                })}
              >
                {(f.options || []).map((o) => {
                  const on = chosen === o.value;

                  return (
                    <SuiBox
                      component="button"
                      type="button"
                      key={o.value}
                      aria-pressed={on}
                      /**
                       * Clicking the one already chosen clears it. The column
                       * is nullable and an order genuinely can have no answer
                       * yet; without this a tab touched by accident could
                       * never be undone.
                       */
                      onClick={() => setField(f.name, on ? "" : o.value)}
                      sx={({ palette, typography, functions: { rgba } }) => ({
                        flex: 1,
                        minWidth: 0,
                        py: 1.25,
                        px: 2,
                        border: 0,
                        cursor: "pointer",
                        textAlign: "center",
                        fontFamily: typography.fontFamily,
                        backgroundColor: on ? rgba(palette[o.tone].main, 0.12) : "transparent",
                        color: on ? palette[o.tone].main : rgba(palette.dark.main, 0.6),
                        // the underline is what makes it a tab rather than a
                        // button, and it is held clear so nothing shifts
                        borderBottom: `3px solid ${on ? palette[o.tone].main : "transparent"}`,
                        transition: "background-color 150ms ease, color 150ms ease",
                        "&:hover": {
                          backgroundColor: rgba(palette[o.tone].main, on ? 0.16 : 0.06),
                        },
                      })}
                    >
                      <SuiBox sx={{ fontSize: 15, fontWeight: on ? 700 : 600 }}>{o.label}</SuiBox>
                      {o.note ? (
                        <SuiBox sx={{ fontSize: 11.5, opacity: 0.75, whiteSpace: "normal" }}>
                          {o.note}
                        </SuiBox>
                      ) : null}
                    </SuiBox>
                  );
                })}
              </SuiBox>
            );
          })}

      <DialogContent sx={{ pt: 3 }}>
        {loadingOptions ? (
          <CargoLoader label="Loading options" size={70} />
        ) : (
          <SuiBox
            pt={1}
            sx={{
              display: "grid",
              // two columns on the wide (order) form, one everywhere else
              gridTemplateColumns: config.wide ? { xs: "1fr", sm: "1fr 1fr" } : "1fr",
              columnGap: 3,
              rowGap: 2,
              // full-width rows for the error banner and any long text area
              "& .full-span": { gridColumn: "1 / -1" },
            }}
          >
            {serverError && (
              <SuiBox className="full-span" mb={1}>
                <Alert severity="error">{serverError}</Alert>
              </SuiBox>
            )}

            {optionsError && (
              <SuiBox className="full-span" mb={1}>
                <Alert severity="warning">{`Could not load lists - ${optionsError}`}</Alert>
              </SuiBox>
            )}

            {/* an add/rename/delete that the server turned down - shown on
                the form itself, because the little sheet is gone by then */}
            {manageError && !manage && (
              <SuiBox className="full-span" mb={1}>
                <Alert severity="error" onClose={() => setManageError("")}>
                  {manageError}
                </Alert>
              </SuiBox>
            )}

            {/**
             * Anything the flat field list cannot describe - a file that
             * uploads on its own rather than being submitted with the form.
             * Only on edit: it needs a record to attach itself to.
             */}
            {isEdit && extra && (
              <SuiBox className="full-span" mb={1}>
                {extra}
              </SuiBox>
            )}

            {config.fields.map((f) => {
              /**
               * `editOnly` fields belong to a record that exists already.
               * STATUS is the one: the system sets it to NEW when the order
               * is created, and it is moved on from the orders list.
               */
              if (!isEdit && f.editOnly) return null;

              const common = {
                key: f.name,
                // multiline fields (NOTE, message body) take the full width
                className: f.multiline ? "full-span" : undefined,
                label: f.label,
                value: values[f.name] ?? "",
                onChange: (e) => setField(f.name, e.target.value),
                error: Boolean(errors[f.name]),
                helperText: errors[f.name] || " ",
                fullWidth: true,
                // full-size inputs: these get read and typed into all day, so
                // the compact "small" density was working against the user
                size: "medium",
                InputLabelProps: { shrink: true, sx: { fontSize: 15 } },
                sx: FIELD_SX,
              };

              if (f.type === "select") {
                /**
                 * A choice can come from the server or be fixed in the field
                 * itself. Payment type is two options that will never change
                 * and needed no endpoint of its own.
                 */
                const list = f.options || options[f.name] || [];
                const valueKey = f.optionsFrom ? f.optionsFrom.value : "value";
                const labelKey = f.optionsFrom ? f.optionsFrom.label : "label";

                // the option currently held, found back from the stored id
                const stored = values[f.name];
                let selected = list.find((o) => String(o[valueKey]) === String(stored)) || null;

                /**
                 * A saved value the list no longer offers is still shown, as
                 * itself. Old orders carry CON NO values that are not
                 * containers ("New", "Next", "canceled", or a file number
                 * that was never created); without this they opened blank and
                 * saving the record - for an unrelated edit - quietly dropped
                 * them.
                 */
                if (!selected && stored !== undefined && stored !== null && stored !== "") {
                  selected = { [valueKey]: stored, [labelKey]: String(stored) };
                }

                /**
                 * Some lists are suggestions rather than a closed set - the
                 * telex wording is a text column on the server, not an enum,
                 * and what it offers is only what containers already use. A
                 * field marked `allowNew` therefore takes a typed value as
                 * well, which is how the first one ever gets in.
                 *
                 * Those work in plain strings end to end: the stored value is
                 * the text, so there is no id to look an option back up by.
                 */
                const free = Boolean(f.allowNew);
                const freeList = list.map((o) => String(o[valueKey]));

                /**
                 * A list the office keeps, rather than one the column
                 * dictates. Each choice the server sent carries the id of
                 * its row, which is what makes it renameable and
                 * deleteable from here; a wording that is not on the list
                 * yet is offered at the bottom as "+ Add".
                 */
                const manageable = Boolean(f.manage);

                /**
                 * A list whose rows are records of their own - people, not
                 * wordings. Typing a name that is not on it offers to create
                 * the record from here, on the form that record already has,
                 * rather than sending somebody to another screen and back.
                 */
                const creatable = Boolean(f.createEntity);
                const CREATE = " create";
                const isCreateRow = (o) => Boolean(o) && o[valueKey] === CREATE;
                const rowId = (text) => list.find((o) => String(o[valueKey]) === String(text))?.id;

                // stop the dropdown selecting the row the icon sits on, and
                // get it out of the way before the little sheet opens
                const manageClick = (e, next) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setOpenBox(null);
                  setManageError("");
                  setManage(next);
                };

                /**
                 * A searchable combobox, not a plain dropdown. With 150
                 * customers a select means scrolling a wall of names; here you
                 * type "AKT" or part of the name and it narrows as you go.
                 */
                return (
                  <Autocomplete
                    key={f.name}
                    className={f.multiline ? "full-span" : undefined}
                    freeSolo={free}
                    // a free field holds its own text; only a closed list has
                    // to carry an unknown stored value as an extra option
                    options={
                      // eslint-disable-next-line no-nested-ternary
                      free
                        ? freeList
                        : selected && !list.some((o) => String(o[valueKey]) === String(stored))
                        ? [selected, ...list]
                        : list
                    }
                    value={free ? String(stored ?? "") : selected}
                    size="medium"
                    fullWidth
                    autoHighlight
                    openOnFocus
                    sx={FIELD_SX}
                    // only the manageable boxes are held open from here, so
                    // that renaming or deleting a choice can close them
                    {...(manageable
                      ? {
                          open: openBox === f.name,
                          onOpen: () => setOpenBox(f.name),
                          onClose: () => setOpenBox(null),
                        }
                      : {})}
                    /**
                     * Whatever is typed and is not already a choice gets a
                     * row of its own at the bottom - "+ Add ..." - which
                     * puts it on the list for everyone. Typing it and
                     * saving without that still works, and keeps it on
                     * this container alone.
                     */
                    filterOptions={
                      // eslint-disable-next-line no-nested-ternary
                      creatable
                        ? (all, state) => {
                            const shown = matchOptions(all, state);
                            const typed = state.inputValue.trim();
                            const known = all.some(
                              (o) => String(o[labelKey] ?? "").toLowerCase() === typed.toLowerCase()
                            );
                            const row = { [valueKey]: CREATE, [labelKey]: typed, createNew: typed };

                            /**
                             * Nothing typed - somebody is reading the list
                             * and the person they want is not on it. The row
                             * goes first, where it is found without scrolling
                             * past ninety names.
                             *
                             * Typed - it goes last instead, so the closest
                             * match stays the one the keyboard is on and
                             * Enter picks a person rather than a new form.
                             */
                            if (!typed) return [row, ...shown];

                            return known ? shown : [...shown, row];
                          }
                        : manageable
                        ? (all, state) => {
                            const shown = matchOptions(all, state);
                            const typed = state.inputValue.trim();
                            const known = all.some(
                              (o) => String(o).toLowerCase() === typed.toLowerCase()
                            );

                            return typed && !known ? [...shown, `${NEW_OPTION}${typed}`] : shown;
                          }
                        : undefined
                    }
                    renderOption={
                      // eslint-disable-next-line no-nested-ternary
                      creatable
                        ? (props, option) =>
                            isCreateRow(option) ? (
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              <li {...props} key={CREATE} style={{ fontWeight: 700 }}>
                                <Icon fontSize="small" sx={{ mr: 1 }}>
                                  person_add
                                </Icon>
                                {option.createNew
                                  ? `Add "${option.createNew}" as a new user`
                                  : "Add a new user"}
                              </li>
                            ) : (
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              <li {...props} key={option[valueKey]}>
                                {String(option[labelKey] ?? "")}
                              </li>
                            )
                        : manageable
                        ? (props, option) => {
                            const text = String(option);

                            if (text.startsWith(NEW_OPTION)) {
                              const typed = text.slice(NEW_OPTION.length);

                              return (
                                <li {...props} key={text}>
                                  <Icon fontSize="small" sx={{ mr: 1 }}>
                                    add
                                  </Icon>
                                  {`Add "${typed}" to the list`}
                                </li>
                              );
                            }

                            const id = rowId(text);

                            return (
                              <li
                                {...props}
                                key={text}
                                style={{ display: "flex", alignItems: "center", gap: 8 }}
                              >
                                <span style={{ flex: 1, minWidth: 0 }}>{text}</span>

                                {/**
                                 * Renaming and deleting the choice itself.
                                 * Hidden from the accessibility tree on
                                 * purpose: they are a shortcut on top of a
                                 * plain list of options, and an option that
                                 * reads out as "released edit delete" is
                                 * worse than one that reads out as
                                 * "released".
                                 */}
                                {id ? (
                                  <SuiBox
                                    aria-hidden
                                    display="flex"
                                    gap={0.5}
                                    sx={{ flexShrink: 0, opacity: 0.55, "&:hover": { opacity: 1 } }}
                                  >
                                    <Tooltip title={`Rename "${text}"`}>
                                      <Icon
                                        fontSize="small"
                                        onClick={(e) =>
                                          manageClick(e, {
                                            mode: "rename",
                                            field: f,
                                            id,
                                            value: text,
                                            text,
                                          })
                                        }
                                        sx={({ palette }) => ({
                                          cursor: "pointer",
                                          "&:hover": { color: palette.info.main },
                                        })}
                                      >
                                        edit
                                      </Icon>
                                    </Tooltip>
                                    <Tooltip title={`Delete "${text}"`}>
                                      <Icon
                                        fontSize="small"
                                        onClick={(e) =>
                                          manageClick(e, {
                                            mode: "delete",
                                            field: f,
                                            id,
                                            value: text,
                                            text,
                                          })
                                        }
                                        sx={({ palette }) => ({
                                          cursor: "pointer",
                                          "&:hover": { color: palette.error.main },
                                        })}
                                      >
                                        delete
                                      </Icon>
                                    </Tooltip>
                                  </SuiBox>
                                ) : null}
                              </li>
                            );
                          }
                        : undefined
                    }
                    // roomier rows in the dropdown, and a taller list before
                    // it starts scrolling
                    ListboxProps={{ style: { maxHeight: 320, fontSize: 15 } }}
                    noOptionsText={free ? "Type to add one" : "No results"}
                    loadingText="Loading..."
                    getOptionLabel={(o) => {
                      const text = typeof o === "string" ? o : String(o?.[labelKey] ?? "");

                      // the "+ Add" row reads as the wording it carries, so
                      // that is what stays in the box while it is being added
                      return text.startsWith(NEW_OPTION) ? text.slice(NEW_OPTION.length) : text;
                    }}
                    isOptionEqualToValue={(o, v) =>
                      typeof o === "string"
                        ? o === v
                        : String(o[valueKey]) === String(v?.[valueKey])
                    }
                    // typing counts on a free field - waiting for Enter or a
                    // click would throw away what was just written
                    onInputChange={
                      free
                        ? (e, text, reason) => {
                            if (reason === "input") setField(f.name, text);
                          }
                        : undefined
                    }
                    onChange={(e, chosen) => {
                      // the "+ Add" row is a request to create the record,
                      // not a value: nothing is stored until it exists
                      if (isCreateRow(chosen)) {
                        setOpenBox(null);
                        setCreating({ field: f, label: chosen.createNew });
                        return;
                      }

                      // freeSolo hands back the raw text when it is new
                      if (typeof chosen === "string") {
                        // ...or the "+ Add" row, which is a request to put
                        // that text on the list rather than a choice
                        if (chosen.startsWith(NEW_OPTION)) {
                          setOpenBox(null);
                          addOption(f, chosen.slice(NEW_OPTION.length));
                          return;
                        }
                        setField(f.name, chosen.trim());
                        return;
                      }
                      setField(f.name, chosen ? chosen[valueKey] : "");
                      applyFills(f, chosen);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={f.label}
                        error={Boolean(errors[f.name])}
                        helperText={errors[f.name] || " "}
                        InputLabelProps={{ shrink: true, sx: { fontSize: 15 } }}
                        placeholder={
                          // eslint-disable-next-line no-nested-ternary
                          manageable
                            ? "Type or pick - the list is yours to edit"
                            : // eslint-disable-next-line no-nested-ternary
                            free
                            ? "Type or pick..."
                            : list.length
                            ? "Type to search..."
                            : "No options"
                        }
                      />
                    )}
                  />
                );
              }

              /**
               * A two-way choice, shown as the two answers side by side
               * rather than as a dropdown that has to be opened to find out
               * which one an order is. Each carries its own colour - the one
               * the orders table tints that order's row with - so the form
               * and the list say the same thing the same way.
               */
              if (f.type === "choice") {
                // the pinned ones are drawn above the form, not in it
                if (f.atTop) return null;

                const chosen = String(values[f.name] ?? "");

                return (
                  <SuiBox
                    key={f.name}
                    className={f.multiline ? "full-span" : undefined}
                    display="flex"
                    flexDirection="column"
                    gap={0.75}
                  >
                    <SuiTypography sx={{ fontSize: 15, opacity: 0.75 }}>{f.label}</SuiTypography>

                    <SuiBox display="flex" gap={1}>
                      {(f.options || []).map((o) => {
                        const on = chosen === o.value;

                        return (
                          <SuiBox
                            component="button"
                            type="button"
                            key={o.value}
                            aria-pressed={on}
                            /**
                             * Clicking the one already chosen clears it. The
                             * column is nullable and an order genuinely can
                             * have no answer yet; without this, a tab touched
                             * by accident could never be undone.
                             */
                            onClick={() => setField(f.name, on ? "" : o.value)}
                            sx={({ palette, borders, typography, functions: { rgba } }) => ({
                              flex: 1,
                              minWidth: 0,
                              minHeight: 52,
                              px: 1.5,
                              py: 1,
                              cursor: "pointer",
                              textAlign: "center",
                              fontFamily: typography.fontFamily,
                              borderRadius: borders.borderRadius.lg,
                              border: `${on ? 2 : 1}px solid ${rgba(
                                palette[o.tone].main,
                                on ? 0.9 : 0.25
                              )}`,
                              backgroundColor: on
                                ? rgba(palette[o.tone].main, 0.14)
                                : "transparent",
                              color: on ? palette[o.tone].main : rgba(palette.dark.main, 0.7),
                              transition: "background-color 150ms ease, border-color 150ms ease",
                              "&:hover": {
                                backgroundColor: rgba(palette[o.tone].main, on ? 0.2 : 0.07),
                              },
                            })}
                          >
                            <SuiBox sx={{ fontSize: 15, fontWeight: on ? 700 : 600 }}>
                              {o.label}
                            </SuiBox>
                            {o.note ? (
                              <SuiBox sx={{ fontSize: 11.5, opacity: 0.7, whiteSpace: "normal" }}>
                                {o.note}
                              </SuiBox>
                            ) : null}
                          </SuiBox>
                        );
                      })}
                    </SuiBox>

                    {/* the same reserved line every other field keeps, so a
                        row with a choice in it is no taller than its neighbour */}
                    <SuiTypography
                      sx={({ palette }) => ({
                        fontSize: 12,
                        minHeight: 18,
                        color: palette.error.main,
                      })}
                    >
                      {errors[f.name] || " "}
                    </SuiTypography>
                  </SuiBox>
                );
              }

              if (f.type === "file") {
                const picked = values[f.name] ? Array.from(values[f.name]) : [];
                return (
                  <SuiBox key={f.name} display="flex" flexDirection="column" gap={0.75}>
                    <SuiTypography variant="caption" fontWeight="bold">
                      {f.label}
                    </SuiTypography>
                    <input
                      type="file"
                      multiple={Boolean(f.multiple)}
                      accept="image/*"
                      onChange={(e) => setField(f.name, e.target.files)}
                      style={{ fontFamily: "inherit", fontSize: 13 }}
                    />
                    {picked.length > 0 && (
                      <SuiTypography variant="caption" sx={{ opacity: 0.7 }}>
                        {`${picked.length} file(s) selected`}
                      </SuiTypography>
                    )}
                  </SuiBox>
                );
              }

              const inputType = ["password", "date", "number"].includes(f.type) ? f.type : "text";

              return (
                <TextField
                  {...common}
                  type={inputType}
                  inputProps={f.type === "number" ? { step: "any" } : undefined}
                  multiline={Boolean(f.multiline)}
                  minRows={f.multiline ? 3 : undefined}
                />
              );
            })}
          </SuiBox>
        )}
      </DialogContent>

      {/* one clear primary action in gold, and a cancel that stays out of
          its way - the two used to look like the same weight */}
      <DialogActions
        sx={({ palette, functions: { rgba } }) => ({
          px: 3,
          pb: 2.5,
          pt: 1,
          gap: 1.5,
          borderTop: `1px solid ${rgba(palette.dark.main, 0.07)}`,
        })}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          size="large"
          sx={({ palette, functions: { rgba } }) => ({
            color: rgba(palette.dark.main, 0.6),
            minWidth: 120,
            fontSize: 15,
            "&:hover": { backgroundColor: rgba(palette.dark.main, 0.05) },
          })}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={submit}
          disabled={saving || loadingOptions}
          sx={{ minWidth: 170, fontSize: 15 }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>

      {/**
       * The record behind a dropdown row, created from the box that offers
       * it. The same form that entity has everywhere else - a user needs an
       * email, a password and a role, which is more than a dropdown can ask
       * for - and when it saves, the list is re-read and the new row is the
       * one left chosen.
       */}
      {creating && (
        <AddDialog
          open
          entity={creating.field.createEntity}
          defaults={creating.label ? { name: creating.label } : undefined}
          onClose={() => setCreating(null)}
          onCreated={(saved) => {
            // the name as it was actually saved, which is not always the one
            // that was typed into the box - the form is editable
            const label = String(saved?.name ?? creating.label ?? "").trim();

            setPendingPick({ field: creating.field.name, label });
            setCreating(null);
            reloadOptions();
          }}
        />
      )}

      {/**
       * Renaming or removing one of the choices. A sheet of its own rather
       * than an edit in place, because both of these reach past this form:
       * the wording belongs to every container that carries it, and the
       * server says how many were touched.
       */}
      <Dialog
        open={Boolean(manage)}
        onClose={manageBusy ? undefined : closeManage}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <SuiTypography variant="h6" fontWeight="bold">
            {manage?.mode === "delete" ? `Delete "${manage?.value}"?` : `Rename "${manage?.value}"`}
          </SuiTypography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {manageError && (
            <SuiBox mb={1.5}>
              <Alert severity="error">{manageError}</Alert>
            </SuiBox>
          )}

          {manage?.mode === "delete" ? (
            <SuiTypography variant="body2" sx={{ opacity: 0.8 }}>
              It comes off the list for everyone. Containers carrying it keep their own data - they
              are simply left with this field empty.
            </SuiTypography>
          ) : (
            <TextField
              autoFocus
              fullWidth
              label={manage?.field?.label}
              value={manage?.text ?? ""}
              onChange={(e) => setManage((m) => ({ ...m, text: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !manageBusy) {
                  e.preventDefault();
                  renameOption(manage.field, manage.id, manage.value, manage.text);
                }
              }}
              InputLabelProps={{ shrink: true }}
              helperText="Every container using the old wording is moved onto the new one"
              sx={FIELD_SX}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={closeManage} disabled={manageBusy} sx={{ minWidth: 100 }}>
            Cancel
          </Button>
          {manage?.mode === "delete" ? (
            <Button
              variant="contained"
              color="error"
              disabled={manageBusy}
              onClick={() => deleteOption(manage.field, manage.id, manage.value)}
              sx={{ minWidth: 120 }}
            >
              {manageBusy ? "Deleting..." : "Delete"}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              disabled={manageBusy}
              onClick={() => renameOption(manage.field, manage.id, manage.value, manage.text)}
              sx={{ minWidth: 120 }}
            >
              {manageBusy ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

AddDialog.defaultProps = {
  onCreated: () => {},
  recordId: null,
  record: null,
  defaults: null,
  tabs: null,
  extra: null,
};

AddDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  entity: PropTypes.oneOf(Object.keys(entities)).isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  // when set, the dialog loads that record and updates instead of creating
  recordId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // optional: the row itself, used to prefill without re-fetching it
  // eslint-disable-next-line react/forbid-prop-types
  record: PropTypes.object,
  // optional: starting values for a new record
  // eslint-disable-next-line react/forbid-prop-types
  defaults: PropTypes.object,
  /**
   * optional: several forms behind one dialog, picked with a tab. Each entry
   * carries its own entity and record, so `entity`/`recordId` above describe
   * the single-form case only. A tab with no record to open is passed
   * `disabled` rather than dropped, so the row always looks the same.
   */
  /**
   * optional: a node rendered at the top of the form when editing. For the
   * parts of a record that are not form fields - an upload that saves
   * itself the moment it is chosen.
   */
  extra: PropTypes.node,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      entity: PropTypes.oneOf(Object.keys(entities)).isRequired,
      label: PropTypes.string.isRequired,
      recordId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      // eslint-disable-next-line react/forbid-prop-types
      record: PropTypes.object,
      // eslint-disable-next-line react/forbid-prop-types
      defaults: PropTypes.object,
      disabled: PropTypes.bool,
    })
  ),
};

export default AddDialog;
